// src/Pages/Admin/Sesion/FotosGrid.js
// Grilla de fotos de la sesión con la identidad dark glass + magenta del admin.
// Toda la escritura de datos pasa por `utils/fotosService`.
import React, { useState, useRef, useEffect } from "react";
import {
  Trash2,
  ArrowLeft,
  ArrowRight,
  Star,
  Pencil,
  X,
  Check,
  AlertTriangle,
} from "lucide-react";
import {
  borrarFoto,
  reordenarFoto,
  marcarDestacada,
  guardarDescripcion,
} from "../../../utils/fotosService";

const FotosGrid = ({ fotos = [], loading, onChange, onEliminarSesion }) => {
  const [editandoId, setEditandoId] = useState(null);
  const [borrandoId, setBorrandoId] = useState(null);
  const [editandoTexto, setEditandoTexto] = useState("");
  const [feedback, setFeedback] = useState(null);

  const listaOrdenada = [...fotos].sort((a, b) => a.orden - b.orden);

  // Un slider por fila: se resetea cuando cambia la lista.
  const [ranges, setRanges] = useState({});
  useEffect(() => {
    setRanges((prev) => {
      const next = {};
      listaOrdenada.forEach((f, i) => {
        next[f.id] = prev[f.id] ?? i;
      });
      return next;
    });
  }, [fotos.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const rangosRef = useRef(ranges);
  rangosRef.current = ranges;

  const cambiarRango = (id, valor) => {
    setRanges((prev) => ({ ...prev, [id]: valor }));
  };

  const mover = async (foto, delta) => {
    const res = await reordenarFoto(listaOrdenada, foto.id, delta);
    if (!res.ok) {
      setFeedback({ tipo: "error", texto: res.error });
      return;
    }
    onChange?.();
  };

  const toggleDestacada = async (foto) => {
    const res = await marcarDestacada(foto, { exclusiva: true });
    if (!res.ok) {
      setFeedback({ tipo: "error", texto: res.error });
      return;
    }
    onChange?.();
  };

  const confirmarBorrado = async (foto) => {
    setBorrandoId(foto.id);
    const res = await borrarFoto(foto);
    setBorrandoId(null);

    if (!res.ok) {
      setFeedback({ tipo: "error", texto: res.error });
      return;
    }

    setFeedback({
      tipo: "exito",
      texto: res.archivoOrfano
        ? "Foto borrada. No se pudo identificar el archivo en el storage, así que puede quedar una copia huérfana."
        : "Foto borrada.",
    });
    onChange?.();
  };

  const guardarTexto = async (fotoId) => {
    const res = await guardarDescripcion(fotoId, editandoTexto);
    setEditandoId(null);
    if (!res.ok) {
      setFeedback({ tipo: "error", texto: res.error });
      return;
    }
    setFeedback({ tipo: "exito", texto: "Descripción guardada." });
    onChange?.();
  };

  return (
    <div className="sg">
      {feedback && (
        <div className={`sg-feedback sg-feedback--${feedback.tipo}`} role="status">
          {feedback.tipo === "exito" ? <Check size={16} /> : <AlertTriangle size={16} />}
          <span>{feedback.texto}</span>
          <button
            type="button"
            className="sg-feedback-close"
            onClick={() => setFeedback(null)}
            aria-label="Cerrar aviso"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {loading ? (
        <p className="sg-loading">Cargando fotos...</p>
      ) : listaOrdenada.length === 0 ? (
        <div className="sg-empty">
          <AlertTriangle size={28} aria-hidden="true" />
          <p>Esta sesión todavía no tiene fotos. Usá el panel de carga para subirlas.</p>
        </div>
      ) : (
        <>
          <div className="sg-grid">
            {listaOrdenada.map((foto, idx) => {
              const rango = rangosRef.current[foto.id] ?? idx;
              const esUltima = idx === listaOrdenada.length - 1;
              const enBorrado = borrandoId === foto.id;

              return (
                <figure
                  key={foto.id}
                  className={`sg-item${foto.destacada ? " sg-item--destacada" : ""}`}
                >
                  <div className="sg-thumb">
                    <img
                      src={foto.url_foto}
                      alt={foto.descripcion || `Foto ${idx + 1}`}
                      loading="lazy"
                    />

                    {foto.destacada && (
                      <span className="sg-badge">
                        <Star size={12} /> Destacada
                      </span>
                    )}

                    <div className="sg-hover">
                      <button
                        type="button"
                        className="sg-hover-btn"
                        onClick={() => toggleDestacada(foto)}
                        title={foto.destacada ? "Quitar destacada" : "Marcar como destacada"}
                      >
                        <Star size={16} />
                      </button>
                      <button
                        type="button"
                        className="sg-hover-btn"
                        onClick={() => {
                          setEditandoId(foto.id);
                          setEditandoTexto(foto.descripcion || "");
                        }}
                        title="Editar descripción"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        type="button"
                        className="sg-hover-btn sg-hover-btn--danger"
                        onClick={() => setBorrandoId(foto.id)}
                        title="Borrar foto"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>

                  {editandoId === foto.id ? (
                    <div className="sg-edit">
                      <input
                        type="text"
                        className="sg-edit-input"
                        value={editandoTexto}
                        onChange={(e) => setEditandoTexto(e.target.value)}
                        placeholder="Descripción de la foto"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "Enter") guardarTexto(foto.id);
                          if (e.key === "Escape") setEditandoId(null);
                        }}
                      />
                      <button
                        type="button"
                        className="sg-icon-btn sg-icon-btn--ok"
                        onClick={() => guardarTexto(foto.id)}
                        aria-label="Guardar descripción"
                      >
                        <Check size={15} />
                      </button>
                      <button
                        type="button"
                        className="sg-icon-btn"
                        onClick={() => setEditandoId(null)}
                        aria-label="Cancelar"
                      >
                        <X size={15} />
                      </button>
                    </div>
                  ) : (
                    <figcaption className="sg-caption">
                      {foto.descripcion || <em>Sin descripción</em>}
                    </figcaption>
                  )}

                  <div className="sg-controls">
                    <button
                      type="button"
                      className="sg-icon-btn"
                      onClick={() => mover(foto, -1)}
                      disabled={idx === 0}
                      aria-label="Mover foto antes"
                      title="Mover antes"
                    >
                      <ArrowLeft size={14} />
                    </button>

                    <input
                      type="range"
                      className="sg-slider"
                      min={0}
                      max={listaOrdenada.length - 1}
                      value={rango}
                      onChange={(e) => cambiarRango(foto.id, Number(e.target.value))}
                      onMouseUp={() => mover(foto, rango - idx)}
                      onTouchEnd={() => mover(foto, rango - idx)}
                      aria-label={`Posición de la foto ${idx + 1}`}
                    />

                    <button
                      type="button"
                      className="sg-icon-btn"
                      onClick={() => mover(foto, 1)}
                      disabled={esUltima}
                      aria-label="Mover foto después"
                      title="Mover después"
                    >
                      <ArrowRight size={14} />
                    </button>

                    <span className="sg-pos">{idx + 1}</span>
                  </div>

                  {borrandoId === foto.id && (
                    <div className="sg-confirm" role="alertdialog">
                      <p>¿Borrar esta foto de forma permanente?</p>
                      <div className="sg-confirm-actions">
                        <button
                          type="button"
                          className="sg-btn sg-btn--danger"
                          onClick={() => confirmarBorrado(foto)}
                          disabled={enBorrado}
                        >
                          {enBorrado ? "Borrando..." : "Sí, borrar"}
                        </button>
                        <button
                          type="button"
                          className="sg-btn sg-btn--ghost"
                          onClick={() => setBorrandoId(null)}
                          disabled={enBorrado}
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  )}
                </figure>
              );
            })}
          </div>

          <div className="sg-foot">
            <p className="sg-count">
              {listaOrdenada.length} foto{listaOrdenada.length === 1 ? "" : "s"}
            </p>
            {onEliminarSesion && (
              <button
                type="button"
                className="sg-btn sg-btn--danger-outline"
                onClick={onEliminarSesion}
              >
                <Trash2 size={15} /> Eliminar sesión
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default FotosGrid;